import React, { useEffect, useContext } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import StoreDataContext from "src/app/context/store-data";
import sdkProvider from "src/utilities/sdkProvider";
import noticeError from "src/app/utilities/noticeError";
import useScriptTag from "src/app/hooks/useScriptTag";
import useLinkTag from "src/app/hooks/useLinkTag";
import getCustomerAuthHeader from "src/app/utilities/getCustomerAuthHeader";
import ChatBotIdentityErrorMessage from "src/app/components/ChatBotIdentityErrorMessage";
import ChatBotNuanceErrorMessage from "src/app/components/ChatBotNuanceErrorMessage";
import initialiseNuance from "./helpers/initialiseNuance";
import styles from "./styles.module.css";
import getQueryParamValue from "src/utilities/getQueryParamValue";
import { trackEvent } from "src/app/utilities/optimizely";
import { EXPERIMENTS } from "src/app/utilities/experiments";
import { getVaStore } from "./helpers/vaStore";

export default function ChatBot({
  status,
  isActive = false,
  onStatusChange,
  shouldTrack,
}) {
  const {
    nuanceDataCenter,
    nuanceTagServerName,
    nuanceVendorUUID,
    nuanceAssetsDomain,
  } = useContext(StoreDataContext);
  const sdkScriptSrc = `${nuanceAssetsDomain}/nuan_websdk_bootstrap.min.0.0.3.js`;
  const sdkLinkSrc = `${nuanceAssetsDomain}/nuance-chat.min.0.0.3.css`;
  const c2cSciptSrc = `${nuanceAssetsDomain}/nuan-c2c.0.0.3.js`;
  const c2cLinkSrc = `${nuanceAssetsDomain}/nuance-c2c-button.0.0.3.css`;
  const sdkScriptTagStatus = useScriptTag(sdkScriptSrc);
  const sdkLinkTagStatus = useLinkTag(sdkLinkSrc);
  const c2cScriptStatus = useScriptTag(c2cSciptSrc);
  const c2cLinkStatus = useLinkTag(c2cLinkSrc);
  const assetsLoaded = [
    sdkScriptTagStatus,
    sdkLinkTagStatus,
    c2cScriptStatus,
    c2cLinkStatus,
  ].every((value) => value === "ready");

  const buttonClasses = classNames({
    [`${styles.hidden}`]: isActive,
  });
  const frameClasses = classNames({
    [`${styles.chatFrame}`]: true,
    [`${styles.hidden}`]: !isActive,
  });

  async function onMessage(event) {
    const origins = [
      "https://asos.digital.nuance.com",
      "https://asos-preprod.lle.digital.nuance.com",
      "https://asos-stage.lle.digital.nuance.com",
    ];

    if (origins.indexOf(event.origin) < 0) return;

    const data = JSON.parse(event.data);
    const loginTrigger = data?.trigger === "AsosLogin";

    if (loginTrigger) {
      const identity = await sdkProvider("identity");

      identity.login();
    }
  }

  useEffect(() => {
    async function init() {
      if (assetsLoaded) {
        try {
          const authHeader = await getCustomerAuthHeader();
          const accessToken = authHeader.replace("Bearer ", "");
          const identity = await sdkProvider("identity");
          const customerId = await identity.customer.customerId();
          const isAuthenticated = await identity.customer.isAuthenticated();
          const browseCountry = window.asos.webContext.getBrowseCountry();
          const browseLanguage = window.asos.webContext.getBrowseLanguage();
          const floor = window.asos.webContext.getFloor();
          const storeCode = getVaStore();
          const referrer = document.referrer;
          const shop = getQueryParamValue("va-shop") || "asos";

          let nuanceData = {
            browseCountry,
            browseLanguage,
            customerId,
            floor,
            isAuthenticated,
            referrer,
            storeCode,
            accessToken,
            shop,
          };

          if (isAuthenticated) {
            const firstName = await identity.customer.firstName();
            const lastName = await identity.customer.lastName();
            nuanceData = { ...nuanceData, firstName, lastName };
          }

          await initialiseNuance(
            nuanceDataCenter,
            nuanceTagServerName,
            nuanceVendorUUID,
            nuanceData,
            onStatusChange
          );

          if (shouldTrack) {
            trackEvent(EXPERIMENTS.SIERRA_CHAT.EVENTS.NUANCE);
          }
        } catch (error) {
          noticeError(error);
          const errorType = error.message.toLowerCase().replaceAll(" ", "-");
          onStatusChange(errorType);
        }
      }
    }

    init();

    window.addEventListener("message", onMessage, false);

    return () => {
      window.removeEventListener("message", onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsLoaded]);

  if (status === "identity-timeout") {
    return <ChatBotIdentityErrorMessage />;
  }

  if (status === "nuance-timeout") {
    return <ChatBotNuanceErrorMessage />;
  }

  return (
    <>
      <div
        id="nuance-chat-fixed"
        data-testid="chat-bot"
        className={buttonClasses}
      />
      <div id="nuanMessagingFrame" className={frameClasses} />
    </>
  );
}

ChatBot.displayName = "ChatBot";

ChatBot.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  shouldTrack: PropTypes.bool.isRequired,
};
